/**
 * Ticket category taxonomy — split per support domain.
 *
 * The customer has two ticket streams (Session 13) and they cover different
 * topics (Person A, Session 14):
 *   - APP support   → transactions/payments, the app itself, the account, and
 *                     platform-level issues with a clinic (admin, not medical).
 *   - CLINIC support → the service received: service problems, complications,
 *                     allergic reactions, aftercare.
 *
 * IMPORTANT — backend constraint:
 *   These codes MUST stay within the backend `TicketCategory` enum, which is a
 *   strict Literal and rejects anything else. As of Session 15 the enum is:
 *     general | service_complaint | complication | allergy | payment_issue |
 *     technical | account | other
 *   (`complication` and `allergy` were added so clinic support can offer them
 *   as first-class topics.) To add more later, extend the enum in the backend
 *   `schemas.py` first, then add the code to the relevant array below plus a
 *   matching `*.category.<code>` label in both locales.
 *
 * Labels are resolved per domain via i18n: `support.category.<code>` for app
 * support and `clinicSupport.category.<code>` for clinic support, so the SAME
 * code can read differently in each context. In particular `service_complaint`
 * appears in BOTH domains by design — labelled "Problem with a clinic"
 * (platform/admin) in app support and "Service problem or complication"
 * (medical/service) in clinic support. The two never collide because they live
 * on different ticket streams (separate endpoints/tables), so the domain itself
 * is the disambiguator.
 */

/** Every code the backend accepts (display safety for any stored ticket). */
export const TICKET_CATEGORIES = [
  'general',
  'service_complaint',
  'complication',
  'allergy',
  'payment_issue',
  'technical',
  'account',
  'other',
];

/**
 * App-support topics: transactions/payments, the app, the account, and
 * platform-level issues with a clinic. `service_complaint` here means a
 * *platform/admin* problem with a clinic ("Problem with a clinic") — NOT a
 * medical/service issue, which belongs to clinic support.
 */
export const APP_SUPPORT_CATEGORIES = [
  'general',
  'payment_issue',
  'service_complaint',
  'technical',
  'account',
  'other',
];

/**
 * Clinic-support topics: the service the customer received. As of the backend
 * enum extension (Session 15) `complication` and `allergy` are first-class
 * codes alongside `service_complaint`. Platform codes
 * (payment/technical/account) are excluded — those are app support.
 */
export const CLINIC_SUPPORT_CATEGORIES = [
  'service_complaint',
  'complication',
  'allergy',
  'general',
  'other',
];
